// Cards draw ONLY from /public/video/card-video/ (see videoPoolPlugin in vite.config.js).
import VIDEO_POOL from 'virtual:card-video-pool';
import { getCardBackgroundUrl, getCardIndexFromEl } from './cardBackgrounds.js';

/**
 * Card videos — each carousel card is its own little CRT tuned to a clip.
 *
 *  - Clips come from /public/video/card-video/ — a curated subset, separate
 *    from the background pool in /public/video/.
 *    Pin a specific clip per world in CARD_CLIPS; anything unpinned gets a
 *    random, non-duplicate pick from the pool.
 *  - Muted / inline / looping; paused while the carousel is off-screen so
 *    five decoders aren't burning while the hero is up.
 *  - `retune()` swaps every card to a new clip with a static flash — wired
 *    to the dice so the whole room changes channel together.
 *  - `getCardVisualSource(cardEl)` hands the transition the live <video>
 *    (or the poster image if the clip has no frame yet) so the grow + wipe
 *    use exactly what the card was showing.
 *
 * Live feeds: this is the seam to plug them into — any same-origin (or
 * CORS-enabled) HLS/MP4 URL can go in CARD_CLIPS. Cross-origin streams
 * without CORS will play but taint the canvas, so the grow/wipe would fall
 * back to the poster image for that card.
 */

/** Per-world pins (public-relative). `null` = random from pool. */
const CARD_CLIPS = {
  chotbat: null,
  sequences: null,
  travel: null,
  'mild-concern': null,
  work: null,
};

const REDUCED =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

// One decoder, and only while a card is actually on screen. Five
// preload=auto clips decoding during a finger-scroll is the stutter.
const HANDHELD =
  typeof matchMedia === 'function' &&
  (matchMedia('(pointer: coarse)').matches || matchMedia('(max-width: 720px)').matches);

function pickClips(count, avoid = []) {
  const pool = VIDEO_POOL.filter((s) => !avoid.includes(s));
  const out = [];
  const bag = pool.slice();
  while (out.length < count && bag.length) {
    const i = (Math.random() * bag.length) | 0;
    out.push(bag.splice(i, 1)[0]);
  }
  // Pool smaller than card count — allow repeats rather than blanks.
  while (out.length < count && VIDEO_POOL.length) {
    out.push(VIDEO_POOL[(Math.random() * VIDEO_POOL.length) | 0]);
  }
  return out;
}

export class CardVideos {
  /**
   * @param {HTMLElement[]} cardEls
   * @param {HTMLElement} section — observed for on/off-screen play state
   */
  constructor(cardEls, section) {
    this.cardEls = cardEls;
    this.videos = [];
    this._visible = false;
    this._scrolling = false;
    this._ratios = new Map();
    this._io = null;
    this._cardIo = null;
    if (!VIDEO_POOL.length || REDUCED) return;

    const random = pickClips(cardEls.length, Object.values(CARD_CLIPS).filter(Boolean));
    cardEls.forEach((el, i) => {
      const v = document.createElement('video');
      v.className = 'card__video';
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      // Handheld used to stay on the SVG poster (preload none, one clip
      // playing). The open animation samples the <video> itself, so the
      // card and the transition were two different pictures. Metadata now,
      // and the poster comes off as soon as a real frame exists.
      v.preload = HANDHELD ? 'metadata' : 'auto';
      v.poster = getCardBackgroundUrl(getCardIndexFromEl(el));
      v.setAttribute('aria-hidden', 'true');
      v.src = CARD_CLIPS[el.dataset.world] || random[i];
      if (HANDHELD) v.addEventListener('loadeddata', () => v.removeAttribute('poster'));
      el.insertBefore(v, el.firstChild);
      this.videos.push(v);
    });

    this._observe(section);
    if (HANDHELD) this._observeCards();
  }

  _observe(section) {
    if (typeof IntersectionObserver === 'undefined' || !section) {
      this._setVisible(true);
      return;
    }
    this._io = new IntersectionObserver(
      (entries) => entries.forEach((e) => this._setVisible(e.isIntersecting)),
      { threshold: 0.05, rootMargin: '25% 0px' }
    );
    this._io.observe(section);
  }

  _setVisible(on) {
    this._visible = on;
    if (HANDHELD) {
      this._syncPlay();
      return;
    }
    this.videos.forEach((v) => {
      if (on) v.play?.().catch(() => {});
      else v.pause?.();
    });
  }

  _observeCards() {
    if (typeof IntersectionObserver === 'undefined') return;
    this._cardIo = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const i = this.cardEls.indexOf(e.target);
        if (i >= 0) this._ratios.set(i, e.isIntersecting ? e.intersectionRatio : 0);
      }
      this._syncPlay();
    }, { threshold: [0, 0.15, 0.4, 0.7, 1] });
    this.cardEls.forEach((el) => this._cardIo.observe(el));

    this._onScroll = () => {
      if (!this._scrolling) {
        this._scrolling = true;
        this.videos.forEach((v) => v.pause?.());
      }
      clearTimeout(this._scrollT);
      this._scrollT = setTimeout(() => {
        this._scrolling = false;
        this._syncPlay();
      }, 180);
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  /** How much of card i is inside the viewport. */
  _ratio(i) {
    const cached = this._ratios.get(i);
    if (cached != null) return cached;
    const el = this.cardEls[i];
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    const vis = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
    return r.height ? vis / r.height : 0;
  }

  /**
   * Handheld: every card you can see plays its own clip. The rest stay
   * paused on their last frame (poster already removed) so a tap doesn't
   * swap in a different picture.
   */
  _syncPlay() {
    this.videos.forEach((v, i) => {
      const show = this._visible && !this._scrolling && this._ratio(i) > 0.12;
      if (!show) {
        if (v.readyState >= 2) v.removeAttribute('poster');
        v.pause?.();
        return;
      }
      v.preload = 'auto';
      const go = () => {
        v.removeAttribute('poster');
        v.play?.().catch(() => {});
      };
      if (v.readyState >= 2) go();
      else if (!v._armPlay) {
        v._armPlay = true;
        v.addEventListener('loadeddata', () => {
          v._armPlay = false;
          if (this._visible && !this._scrolling && this._ratio(i) > 0.12) go();
        }, { once: true });
      }
    });
  }

  /**
   * Change every card's channel. Static flash covers the swap; the new clip
   * starts as soon as it has a frame.
   */
  retune() {
    if (!this.videos.length) return;
    const current = this.videos.map((v) => v.getAttribute('src'));
    const next = pickClips(this.videos.length, current);
    this.videos.forEach((v, i) => {
      const card = this.cardEls[i];
      if (CARD_CLIPS[card.dataset.world]) return; // pinned — keep
      card.classList.add('is-retuning');
      v.src = next[i];
      v.load();
      const settle = () => {
        if (this._visible && !HANDHELD) v.play?.().catch(() => {});
        if (HANDHELD) this._syncPlay();
        setTimeout(() => card.classList.remove('is-retuning'), 120 + Math.random() * 160);
      };
      v.addEventListener('loadeddata', settle, { once: true });
      setTimeout(settle, 900); // never stuck in static
    });
  }

  destroy() {
    this._io?.disconnect();
    this._cardIo?.disconnect();
    clearTimeout(this._scrollT);
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    this.videos.forEach((v) => {
      v.pause?.();
      v.removeAttribute('src');
      v.load?.();
      v.remove();
    });
    this.videos = [];
  }
}

/**
 * What the card is visually showing right now — the live video if it has a
 * decoded frame, else the poster image URL. Both the grow and the channel
 * wipe accept either.
 * @returns {{ element: HTMLVideoElement|null, imageUrl: string }}
 */
export function getCardVisualSource(cardEl) {
  const imageUrl = getCardBackgroundUrl(getCardIndexFromEl(cardEl));
  const v = cardEl?.querySelector?.('video.card__video');
  const ok = v && v.readyState >= 2 && v.videoWidth > 0;
  return { element: ok ? v : null, imageUrl };
}

/**
 * Freeze the current frame to a JPEG data URL (for the cross-document
 * handoff). Returns null if there is no frame or the canvas is tainted.
 */
export function captureFrameDataUrl(video, maxW = 960) {
  if (!video || video.readyState < 2 || !video.videoWidth) return null;
  try {
    const scale = Math.min(1, maxW / video.videoWidth);
    const c = document.createElement('canvas');
    c.width = Math.round(video.videoWidth * scale);
    c.height = Math.round(video.videoHeight * scale);
    c.getContext('2d').drawImage(video, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.82);
  } catch {
    return null; // cross-origin without CORS
  }
}
